import React from 'react';
import styled from 'styled-components';
import { LiquidGlassProvider, useLiquidGlassContext, getInvertedVariant } from './LiquidGlassContext';

// Enhanced glass card with variant support - fully transparent by default
const LiquidGlassCardStyled = styled.div`
  background: ${props => {
    const { variant = 'light', theme, $effectiveVariant } = props;
    const effectiveVar = $effectiveVariant || variant;
    if (effectiveVar === 'dark' || (effectiveVar === 'auto' && theme?.isDarkMode)) {
      return 'rgba(15, 23, 42, 0.1)';
    }
    return 'transparent';
  }};
  backdrop-filter: blur(35px) saturate(300%);
  -webkit-backdrop-filter: blur(35px) saturate(300%);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.06),
    0 8px 25px rgba(0, 0, 0, 0.04),
    0 2px 8px rgba(0, 0, 0, 0.02),
    inset 0 2px 0 rgba(255, 255, 255, 0.3),
    inset 0 -2px 0 rgba(0, 0, 0, 0.03);
  position: relative;
  overflow: hidden;
  will-change: transform;
  transform: translateZ(0);
  
  /* Enhanced glass-like depth effect with full transparency */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.15) 0%,
      rgba(255, 255, 255, 0.05) 25%,
      rgba(255, 255, 255, 0.02) 50%,
      rgba(0, 0, 0, 0.01) 75%,
      rgba(0, 0, 0, 0.03) 100%
    );
    pointer-events: none;
    z-index: 1;
  }
  
  /* Enhanced glass reflection effect with transparency */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.12) 0%,
      rgba(255, 255, 255, 0.04) 50%,
      transparent 100%
    );
    pointer-events: none;
    z-index: 2;
  }
  
  padding: ${props => props.compact ? '1.5rem' : '2.5rem'};
  border-radius: ${props => props.borderRadius || '28px'};
  margin: ${props => props.margin || '2rem 0'};
  color: ${props => {
    const { variant = 'light', theme, $effectiveVariant } = props;
    const effectiveVar = $effectiveVariant || variant;
    const isDark = effectiveVar === 'dark' || (effectiveVar === 'auto' && theme?.isDarkMode);
    return isDark ? 'rgba(248, 250, 252, 0.95)' : 'rgba(248, 250, 252, 0.95)';
  }};
  line-height: 1.7;
  font-size: ${props => props.fontSize || '1rem'};
  position: relative;
  z-index: 1;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &:hover {
    /* Dim parent background on hover */
    &::before {
      background: linear-gradient(
        135deg,
        rgba(0, 0, 0, 0.1) 0%,
        rgba(0, 0, 0, 0.05) 25%,
        rgba(0, 0, 0, 0.02) 50%,
        rgba(0, 0, 0, 0.01) 75%,
        rgba(0, 0, 0, 0.03) 100%
      );
    }
    transform: translateY(-8px) translateZ(0) scale(1.02);
    box-shadow: 
      0 30px 80px rgba(0, 0, 0, 0.08),
      0 12px 40px rgba(0, 0, 0, 0.06),
      0 6px 20px rgba(0, 0, 0, 0.04),
      inset 0 3px 0 rgba(255, 255, 255, 0.4),
      inset 0 -3px 0 rgba(0, 0, 0, 0.06);
    backdrop-filter: blur(40px) saturate(350%);
    -webkit-backdrop-filter: blur(40px) saturate(350%);
    border-color: rgba(255, 255, 255, 0.2);
  }
  
  &:hover::after {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.18) 0%,
      rgba(255, 255, 255, 0.06) 50%,
      transparent 100%
    );
  }
  
  /* Enhanced glass material effect */
  &::before {
    border-radius: inherit;
  }
  
  &::after {
    border-radius: ${props => props.borderRadius || '28px'} ${props => props.borderRadius || '28px'} 0 0;
  }
  
  /* Content should appear above glass effects */
  > * {
    position: relative;
    z-index: 3;
  }
  
  /* Typography styles */
  p {
    margin-bottom: 1.5rem;
    line-height: 1.7;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Roboto', sans-serif;
    color: ${props => {
    const { variant = 'light', theme, $effectiveVariant } = props;
    const effectiveVar = $effectiveVariant || variant;
    const isDark = effectiveVar === 'dark' || (effectiveVar === 'auto' && theme?.isDarkMode);
    return isDark ? '#93C5FD' : '#B91C1C';
  }};
    font-weight: 600;
    line-height: 1.3;
    margin-top: 0;
    text-shadow: ${props => {
    const { variant = 'light', theme, $effectiveVariant } = props;
    const effectiveVar = $effectiveVariant || variant;
    const isDark = effectiveVar === 'dark' || (effectiveVar === 'auto' && theme?.isDarkMode);
    return isDark ? '1px 1px 3px rgba(0, 0, 0, 0.5)' : 'none';
  }};
  }

  h2 {
    margin: 2rem 0 1.5rem 0;
    font-size: 1.8rem;
    
    &:first-child {
      margin-top: 0;
    }
  }

  h3 {
    margin: 1.5rem 0 1rem 0;
    font-size: 1.4rem;
  }

  h4 {
    margin: 1rem 0 0.75rem 0;
    font-size: 1.2rem;
  }

  /* List styles */
  ol, ul {
    margin: 1.5rem 0;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.75rem;
      color: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? 'rgba(248, 250, 252, 0.9)' : 'rgba(248, 250, 252, 0.9)';
  }};
      line-height: 1.6;
      text-shadow: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? '1px 1px 3px rgba(0, 0, 0, 0.3)' : 'none';
  }};
      
      &:last-child {
        margin-bottom: 0;
      }
      
      ul, ol {
        margin: 0.5rem 0;
        padding-left: 1.25rem;
        
        li {
          margin-bottom: 0.5rem;
        }
      }

      strong {
        color: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? '#93C5FD' : '#B91C1C';
  }};
        font-weight: 600;
        text-shadow: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? '1px 1px 3px rgba(0, 0, 0, 0.5)' : 'none';
  }};
      }
    }
  }

  /* Custom bullet points for unordered lists */
  ul {
    list-style: none;
    padding-left: 0;
    
    li {
      padding-left: 1.5rem;
      position: relative;
      
      &::before {
        content: "•";
        color: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? '#93C5FD' : '#B91C1C';
  }};
        font-weight: bold;
        position: absolute;
        left: 0;
        top: 0;
      }
      
      ul li::before {
        content: "◦";
        color: #1E3A8A;
      }
    }
  }
  
  /* Links */
  a {
    color: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? '#93C5FD' : '#B91C1C';
  }};
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? '#FFFFFF' : '#1E3A8A';
  }};
      text-decoration: underline;
    }
  }
  
  /* Code blocks */
  code {
    background: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? 'rgba(147, 197, 253, 0.2)' : 'rgba(30, 64, 175, 0.1)';
  }};
    color: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? '#E2E8F0' : 'inherit';
  }};
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-family: 'Monaco', 'Menlo', monospace;
    font-size: 0.9em;
  }
  
  pre {
    background: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)';
  }};
    color: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? '#E2E8F0' : 'inherit';
  }};
    padding: 1rem;
    border-radius: 8px;
    overflow-x: auto;
    margin: 1.5rem 0;
    
    code {
      background: none;
      padding: 0;
    }
  }
  
  @media (max-width: 768px) {
    padding: ${props => props.compact ? '1rem' : '1.5rem'};
    font-size: 0.95rem;
    
    h2 {
      font-size: 1.5rem;
    }
    
    h3 {
      font-size: 1.25rem;
    }
  }
`;

// Enhanced ServiceTile component with built-in liquid glass features - variant support
export const ServiceTile = styled.div`
  background: ${props => {
    const { variant = 'light', theme, $effectiveVariant } = props;
    const effectiveVar = $effectiveVariant || variant;
    if (effectiveVar === 'dark' || (effectiveVar === 'auto' && theme?.isDarkMode)) {
      return 'rgba(15, 23, 42, 0.1)';
    }
    return 'transparent';
  }};
  backdrop-filter: blur(35px) saturate(300%);
  -webkit-backdrop-filter: blur(35px) saturate(300%);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.06),
    0 8px 25px rgba(0, 0, 0, 0.04),
    0 2px 8px rgba(0, 0, 0, 0.02),
    inset 0 2px 0 rgba(255, 255, 255, 0.3),
    inset 0 -2px 0 rgba(0, 0, 0, 0.03);
  position: relative;
  overflow: hidden;
  will-change: transform;
  transform: translateZ(0);
  
  /* Enhanced glass-like depth effect with full transparency */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.15) 0%,
      rgba(255, 255, 255, 0.05) 25%,
      rgba(255, 255, 255, 0.02) 50%,
      rgba(0, 0, 0, 0.01) 75%,
      rgba(0, 0, 0, 0.03) 100%
    );
    pointer-events: none;
    z-index: 1;
  }
  
  /* Enhanced glass reflection effect with transparency */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.12) 0%,
      rgba(255, 255, 255, 0.04) 50%,
      transparent 100%
    );
    pointer-events: none;
    z-index: 2;
  }
  
  padding: ${props => props.compact ? '1.5rem' : '2rem'};
  border-radius: ${props => props.borderRadius || '28px'};
  text-decoration: none;
  transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  height: ${props => props.height || 'auto'};
  display: ${props => props.flexLayout ? 'flex' : 'block'};
  flex-direction: ${props => props.flexLayout ? 'column' : 'initial'};
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  position: relative;
  z-index: 1;
  text-align: ${props => props.textAlign || 'center'};

  /* Enhanced hover effects without blue tint - transparent */
  &:hover {
    /* Dim parent background on hover */
    &::before {
      background: linear-gradient(
        135deg,
        rgba(0, 0, 0, 0.1) 0%,
        rgba(0, 0, 0, 0.05) 25%,
        rgba(0, 0, 0, 0.02) 50%,
        rgba(0, 0, 0, 0.01) 75%,
        rgba(0, 0, 0, 0.03) 100%
      );
    }
    transform: translateY(-5px) translateZ(0);
    background: rgba(255, 255, 255, 0.02);
    border-color: rgba(255, 255, 255, 0.15);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(40px) saturate(350%);
    -webkit-backdrop-filter: blur(40px) saturate(350%);
    
    h3, .tile-title {
      color: #B91C1C;
      text-shadow: 0 2px 8px rgba(185, 28, 28, 0.3);
    }
    
    .tile-icon {
      color: #B91C1C;
      transform: scale(1.1);
    }
  }

  /* Active state for clickable tiles */
  ${props => props.clickable && `
    &:active {
      transform: translateY(-2px) scale(0.99) translateZ(0);
    }
  `}

  /* Content positioning above glass effects */
  > * {
    position: relative;
    z-index: 3;
  }

  /* Typography */
  h3, .tile-title {
    font-family: 'Roboto', sans-serif;
    font-size: ${props => props.titleSize || '1.4rem'};
    color: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? 'rgba(248, 250, 252, 0.95)' : 'rgba(248, 250, 252, 0.95)';
  }};
    margin-bottom: 1rem;
    line-height: 1.3;
    font-weight: 600;
    transition: all 0.3s ease;
    min-height: 4.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    text-shadow: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? '1px 1px 3px rgba(0, 0, 0, 0.5)' : 'none';
  }};
  }

  h4 {
    font-family: 'Roboto', sans-serif;
    font-size: 1.1rem;
    color: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? 'rgba(226, 232, 240, 0.9)' : 'rgba(226, 232, 240, 0.9)';
  }};
    margin-bottom: 0.75rem;
    line-height: 1.3;
    font-weight: 500;
    text-shadow: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? '1px 1px 3px rgba(0, 0, 0, 0.3)' : 'none';
  }};
  }

  p, .tile-description {
    color: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? 'rgba(203, 213, 225, 0.9)' : 'rgba(203, 213, 225, 0.9)';
  }};
    line-height: 1.6;
    font-size: ${props => props.textSize || '1rem'};
    margin-bottom: 1.5rem;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6);
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  /* List styles within tiles */
  ul, ol, .tile-details {
    margin: 1.5rem 0;
    padding-left: 0;
    list-style: none;
    flex: 1;
    text-align: left;
    
    li {
      margin-bottom: 0.75rem;
      color: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? 'rgba(203, 213, 225, 0.9)' : 'rgba(203, 213, 225, 0.9)';
  }};
      font-size: 0.9rem;
      line-height: 1.5;
      padding-left: 1rem;
      position: relative;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6);
      
      &:last-child {
        margin-bottom: 0;
      }
      
      &::before {
        content: "•";
        color: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? '#B91C1C' : '#B91C1C';
  }};
        font-weight: bold;
        position: absolute;
        left: 0;
        top: 0;
      }
    }
  }
  
  /* Icon styling */
  .tile-icon {
    font-size: 2rem;
    color: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? '#93C5FD' : '#1E40AF';
  }};
    margin-bottom: 1.5rem;
    display: block;
    transition: all 0.3s ease;
    text-shadow: ${props => {
    const { variant = 'light', theme } = props;
    const isDark = variant === 'dark' || (variant === 'auto' && theme?.isDarkMode);
    return isDark ? '1px 1px 3px rgba(0, 0, 0, 0.5)' : 'none';
  }};
  }
  
  /* Price/badge styling */
  .tile-badge, .tile-price {
    background: rgba(185, 28, 28, 0.1);
    color: #ffffff;
    padding: 12px 20px;
    border-radius: 20px;
    border: 1px solid rgba(185, 28, 28, 0.2);
    font-weight: 700;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: auto;
    transition: all 0.3s ease;
    min-height: 48px;
    text-align: center;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6);
  }
  
  &:hover .tile-badge,
  &:hover .tile-price {
    background: rgba(185, 28, 28, 0.2);
    border-color: rgba(185, 28, 28, 0.3);
    color: #ffffff;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6);
  }
  
  @media (max-width: 768px) {
    padding: ${props => props.compact ? '1rem' : '1.5rem'};
    
    h3, .tile-title {
      font-size: 1.2rem;
    }
    
    .tile-icon {
      font-size: 1.8rem;
    }
  }
  
  @media (prefers-reduced-motion: reduce) {
    &:hover {
      transform: none;
    }
    
    &::before {
      transition: none;
    }
    
    .tile-icon {
      transform: none;
    }
  }
`;

// ServiceTile React component with props-based content
export const ServiceCard = ({
  icon,
  title,
  description,
  details = [],
  price,
  onClick,
  className,
  style,
  compact = false,
  clickable = false,
  variant = 'auto',
  children,
  ...props
}) => {
  const { parentVariant, depth } = useLiquidGlassContext();
  const effectiveVariant = getInvertedVariant(parentVariant, variant);

  return (
    <LiquidGlassProvider variant={effectiveVariant} depth={depth + 1}>
      <ServiceTile
        className={className}
        style={style}
        compact={compact}
        clickable={clickable || !!onClick}
        onClick={onClick}
        flexLayout={true}
        height="100%"
        variant={variant}
        $effectiveVariant={effectiveVariant}
        {...props}
      >
        {icon && <div className="tile-icon">{icon}</div>}
        {title && <h3 className="tile-title">{title}</h3>}
        {description && <p className="tile-description">{description}</p>}
        {details.length > 0 && (
          <ul className="tile-details">
            {details.map((detail, index) => (
              <li key={index}>{detail}</li>
            ))}
          </ul>
        )}
        {children}
        {/* Last: `.tile-price` carries `margin-top: auto`, which only pins it
            to the bottom of the flex column if nothing follows it. */}
        {price && <div className="tile-price">{price}</div>}
      </ServiceTile>
    </LiquidGlassProvider>
  );
};

// Wrapper component that handles context
const LiquidGlassCardComponent = ({ children, variant = 'auto', ...props }) => {
  const { parentVariant, depth } = useLiquidGlassContext();
  const effectiveVariant = getInvertedVariant(parentVariant, variant);

  return (
    <LiquidGlassProvider variant={effectiveVariant} depth={depth + 1}>
      <LiquidGlassCardStyled
        {...props}
        variant={variant}
        $effectiveVariant={effectiveVariant}
      >
        {children}
      </LiquidGlassCardStyled>
    </LiquidGlassProvider>
  );
};

export const LiquidGlassCard = LiquidGlassCardComponent;
export default LiquidGlassCardComponent;
